// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useSessionStore from '../../../../../store/session';
import useRootOrganizationStore from '@/app/store/rootOrganization';
import actionsSession from '@/app/modules/session/api/actionsSession';
// Components
import Dropdown from '../../../../../../common-utils/components/renders/dropdown/Dropdown';
import ActivateDeactivate from '@/app/modules/users/monetic/components/dropdown/actions/ActivateDeactivate';
import ModalPermissions from '@/app/modules/users/monetic/components/modals/permissions/ModalPermissions';
import ModalResetPassword from '@/app/modules/users/monetic/components/modals/ModalResetPassword';
import ModalRemoveUser from '@/app/modules/users/monetic/components/modals/ModalRemoveUser';
// Common
import organizationFields from '@/app/modules/organizations/fields/organizationFields';
import ICONS from '../../../../../../common-utils/enums/icons';
import Permissions from '../../../../../permissions/Permissions';
import merchantUserFields from '../../fields/merchantUserFields';
// Lang
import { nsUser } from '@/app/i18n/namespaces';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import { useNavigate } from 'react-router-dom';
import { buildUrl } from '@/common-utils/functions/restUtils';
import RouteUrls from '@/app/root/enums/RouteUrls';
import TAB_KEYS_HISTORICS from '@/app/root/enums/tabs/HistoricsTabs';
import historicsFields from '@/app/modules/historics/fields/historicsFields';

/**
 *
 * @param root0
 * @param root0.merchantUser
 * @param root0.setLaunchRefetch
 * @param root0.merchantUsersNumber
 * @returns {JSX}
 */
function DropdownMerchantUserActionsRow({ merchantUser, setLaunchRefetch, merchantUsersNumber }) {

    const navigate = useNavigate();

    //Constants
    const merchantUserId = merchantUser ? merchantUser[merchantUserFields.UUID.key] : null;

    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);
    const permissions = useSessionStore(s => s.session.permissions);

    //State
    const [showModalUpdatePermissions, setShowModalUpdatePermissions] = useState(false);
    const [showModalResetPassword, setShowModalResetPassword] = useState(false);
    const [showModalRemoveUser, setShowModalRemoveUser] = useState(false);

    // Api calls
    const submitConnectAs = actionsSession.connectAs(() => { }, () => { });

    return (
        <>
            <Dropdown
                triggerValues={{ variant: 'ghostNoBorder', icon: ICONS.MORE }}
                actions={[
                    ...ActivateDeactivate(merchantUser, permissions, setLaunchRefetch),
                    {
                        icon: ICONS.CONTACT_MAIL,
                        entity: 'actions.row.reset_password',
                        namespace: nsUser,
                        onClick: () => setShowModalResetPassword(true),
                        visible: true,
                        disabled: !hasRequiredPermissions({
                            permissions: permissions,
                            requiredPermissions: [Permissions.USER_RESEND_ACTIVATION_MAIL.name]
                        }),
                        tooltipForbidden: 'title.permissions.access_denied'
                    },
                    {
                        icon: ICONS.USER_SETTINGS,
                        entity: 'actions.row.permissions_update',
                        namespace: nsUser,
                        onClick: () => setShowModalUpdatePermissions(true),
                        visible: true,
                        disabled: !hasRequiredPermissions({
                            permissions: permissions,
                            requiredPermissions: [Permissions.USER_CREATE.name]
                        }),
                        tooltipForbidden: 'title.permissions.access_denied'
                    },
                    {
                        icon: ICONS.SUBSCRIPTION_TERMINATE,
                        entity: 'actions.row.remove',
                        namespace: nsUser,
                        onClick: () => setShowModalRemoveUser(true),
                        visible: true,
                        disabled:
                            !hasRequiredPermissions({
                                permissions: permissions,
                                requiredPermissions: [Permissions.USER_REMOVE.name]
                            }) ||
                            (merchantUsersNumber > 1 && merchantUser[merchantUserFields.IS_ADMIN.key]),
                        tooltipForbidden: 'title.permissions.access_denied'
                    },
                    {
                        icon: ICONS.USER_CONNECT_AS,
                        entity: 'actions.row.connect_as',
                        namespace: nsUser,
                        onClick: () => submitConnectAs.mutate(merchantUserId),
                        visible: true,
                        disabled: !hasRequiredPermissions({
                            permissions: permissions,
                            requiredPermissions: [Permissions.USER_LOGIN_AS_USER.name]
                        }),
                        tooltipForbidden: 'title.permissions.access_denied'
                    },
                    {
                        icon: ICONS.HISTORY,
                        entity: 'actions.row.historics_access',
                        namespace: nsUser,
                        onClick: () => {
                            const userEmail = merchantUser ? merchantUser[merchantUserFields.EMAIL.key] : null;
                            const userHistorics = buildUrl(RouteUrls.HISTORICS.url,
                                {
                                    view: TAB_KEYS_HISTORICS.USERS.name,
                                    filters: userEmail ? [
                                        {
                                            key: historicsFields.USERS_HISTORICS.EMAIL.key,
                                            value: userEmail
                                        }
                                    ] : []
                                }
                            );
                            navigate(userHistorics);
                        },
                        visible: true,
                        disabled: !hasRequiredPermissions({
                            permissions: permissions,
                            requiredPermissions: [Permissions.LIST_HISTORY.name]
                        }),
                        tooltipForbidden: 'title.permissions.access_denied'
                    }
                ]} />
            {
                showModalResetPassword &&
                <ModalResetPassword
                    isOpen={showModalResetPassword}
                    setIsOpen={setShowModalResetPassword}
                    userId={merchantUserId}
                    userEmail={merchantUser[merchantUserFields.EMAIL.key]}
                />
            }
            {
                showModalRemoveUser &&
                <ModalRemoveUser
                    isOpen={showModalRemoveUser}
                    setIsOpen={setShowModalRemoveUser}
                    userId={merchantUserId}
                    userEmail={merchantUser[merchantUserFields.EMAIL.key]}
                    setLaunchRefetch={setLaunchRefetch}
                />
            }
            {
                showModalUpdatePermissions &&
                <ModalPermissions
                    isOpen={showModalUpdatePermissions}
                    setIsOpen={setShowModalUpdatePermissions}
                    userId={merchantUserId}
                    organizationId={rootOrganization[organizationFields.UUID.key]}
                    setLaunchRefetch={setLaunchRefetch}
                    isMerchantUser={true}
                />
            }
        </>
    );
}

DropdownMerchantUserActionsRow.propTypes = {
    merchantUser: PropTypes.object.isRequired,
    setLaunchRefetch: PropTypes.func.isRequired,
    merchantUsersNumber: PropTypes.number
};
export default DropdownMerchantUserActionsRow;